import { SquiggleItem } from "../types/types";

export const systemItems: SquiggleItem[] = [
  {
    id: '000000000000000000000001',
    type: 'system',
    sortTimestamp: 0,
    name: 'SquiggleBoard',
    text: '### Welcome to SquiggleBoard!\n\n' +
'This free app is designed to help you manage information in your tabletop RPG adventures, both as a player and a game master. ' +
'You can create items re^pre^sent^ing quests, people, locations, and more, and link them together by mentioning items by their name, ' +
'or link them explicitly using their *Links* tab.\n\n' +
'SquiggleBoard: Changelog\n\n' +
'***Remember to bookmark this URL so you can find this adventure again!***\n' +
'Also make sure to back up your data regu^larly by using the Download feature.\n\n' +
'### More\n' +
'- SquiggleBoard: Links\n' +
'- SquiggleBoard: Text Formatting\n' +
'- SquiggleBoard: Item Management\n' +
'- SquiggleBoard: Images\n' +
'- SquiggleBoard: Keyboard Commands\n' +
'- SquiggleBoard: Privacy, Security, Legal',
    links: [],
    aliases: [],
    archived: false,
    images: []
  },
  {
    id: '000000000000000000000002',
    type: 'system',
    sortTimestamp: 0,
    name: 'SquiggleBoard: Text Formatting',
    text: 
'You can format the item text using a simple stripped-down *Markdown* syntax as described below.\n\n' +
'Inside any text you can enclose blocks of characters in:\n' +
'- **\\***asterisks**\\*** for *Italic* text\n' +
'- **\\*\\***double asterisks**\\*\\*** for **Bold** text\n' +
'- **\\*\\*\\***triple asterisks**\\*\\*\\*** for ***Both***\n' +
'- **\\`**backticks**\\`** for `Monospace` text.\n' +
'You can even mix them, like `monospace with **bold** inside`.\n\n' +
'To produce a soft hyphen in a long word, use a caret (**`\\^`**) ' +
'where you want the word to be breakable. For example,\n' +
'Super\\^cali\\^fragi\\^listic\\^expiali\\^docious\n' +
'becomes breakable: `Super^cali^fragi^listic^expiali^docious`\n\n' +
'To produce an actual asterisk, caret or other syntax character, preceed it with a backslash (**`\\\\`**), e.g., `\\\\\\*`.\n\n' +
'### Lists\n' +
'At the very start of a line you can use:\n\n' +
'`"**-**"` for an item in a bullet list\n' +
'- This is a bullet list\n' +
'- It is made with *minus signs*\n\n' +
'`"**1.**"` for an item in a numbered list\n' +
'1. This is a numbered list\n' +
'1. It is made with *numbers followed by periods*. The actual number used doesn\'t matter as it will be auto-renumbered.\n\n' +
'### Blocks\n' +
'You can create block quotes by starting a line with a greater-than sign (**`>`**).\n\n' +
'`> This is a block quote.`\n' +
'`> It is rendered on a dark background.`\n\n' +
'becomes\n\n' +
'> This is a block quote.\n' +
'> It is rendered on a dark background.\n\n' +
'### Horizontal lines\n' +
'A line of text consisting only of three minuses (**`---`**) gives a horizontal line:\n' +
'---\n\n' +
'### Tables\n' +
'You can create tables using pipe-delimited lines. For example:\n\n' +
'`**|** Name **|** Value **|**`\n' +
'`**|** Foo **|** Bar **|**`\n\n' +
'becomes:\n\n' +
'| Name | Value |\n' +
'| Foo | Bar |\n\n' +
'You can use text formatting inside the table cells, and you can use a line of dashes\n(**`| --- |`**)\nto make a stronger border between two rows.\n\n' +
'`|\\*\\*Name\\*\\*|\\*\\*\\*Value\\*\\*\\*|\\`Type\\`|`\n' +
'`|Foo|\\*Bar\\*|\\`1\\`|`\n' +
'`|---|---|---|`\n' +
'`|Another|\\*Row\\*|\\`2\\`|`\n\n' +
'becomes:\n\n' +
'|**Name**|***Value***|`Type`|\n' +
'|---|---|---|\n' +
'|Foo|*Bar*|`1`|\n' +
'|Another|*Row*|`2`|\n' +

'## Headings\n' +
'**`#`** at the start of a line makes it a large heading. You can create smaller headings with **`##`**, **`###`** and **`####`**. The heading above is the sec^ond largest, using **`##`**.',
    links: [],
    aliases: [],
    archived: false,
    images: []
  },
  {
    id: '000000000000000000000003',
    type: 'system',
    sortTimestamp: 0,
    name: 'SquiggleBoard: Item Management',
    text:
'### Creating items\n' +
'To create a new item, click the `**+**` button in the top right of one of the type lists, or press `SHIFT+CTRL+i` or `SHIFT+CMD+i` and choose a type. This will create a new item and open it in edit mode.\n\n' +
'### Editing items\n' +
'To edit an open item in view mode, click the Edit button in its toolbar or press `**e**` while it is selected. This will switch the item to edit mode, where you can change its name and text.\n\n' +
'While in edit mode, you can press **Save & edit** to save and continue editing, or **Save & view** to save and switch to view mode. Make sure to save often. The keyboard shortcut for **Save & edit** is `CTRL+Enter` or `CMD+Enter`.\n\n' +
'During edit, if you start typing the name of another item, you will get sug^ges^tions. Press `CTRL+Space` to auto^com^plete the first sug^gestion. Words with matching letter-casing will sug^gest completions earlier than non-matching words.\n\n' +
'### Linking Items\n' +
'Items are automatically linked by men^tion^ing their name in the text of other items. You can add more aliases to an item for the link de^tec^tion by pressing the three dots next to the item name or pressing the `**a**` key while the item is selected.\n\n' +
'Under the *Links* tab of an item you can see all these implicit links, and also add explicit links to other items.\n\n' +
'### Finding items\n' +
'All items created are found in the lists on the left. You can click a specific item type’s tab to list only that type. If you click a tab twice, the lists will be hidden until you click a tab again.\n\n' +
'The lists are sorted in alphabetical order, except Log Entries which are sorted in reverse chronological order.\nYou can also star important items using the \u2605 button in view mode or pressing `**s**`, to have them appear at the top of their respective lists. Finally, by ar^chiv^ing an item using the hour^glass button, you make it appear at the bottom of the list.\n\n' +
'You can also search for items by name or text using the search dialog, accessible from the tool menu in the top right or by pressing `SHIFT+CTRL+f` or `SHIFT+CMD+f`.\n\n' +
'### Hierarchies\n' +
'If you want to express a parent-child relation^ship between items, you can do so by setting an item’s parent in its *Links* tab. This will make the child item appear indented under the parent item in the lists on the left. Note that you can only set a parent of the same type as the child item.\n\n' +
'If the child item is starred, it will appear both at the top of the list and indented beneath its parent. An archived item will only appear at the bottom, not under its parent.\n\n' +
'### The workspace\n' +
'The main area of the application is the work^space, which shows open items. These items are shown in the order they were opened. To clear the area, click the Clear Workspace button in the top right toolbar, or press `SHIFT+CTRL+d` or `SHIFT+CMD+d`.\n\n' +
'The work^space is designed to let you keep multiple items open at once. You can even have multiple items in edit mode at the same time. Clearing the work^space does not clear items in edit mode.\n\n' +
'By clicking an open item it becomes se^lect^ed and will receive key com^mands. Its item ID is also shown in the URL, so you can link directly to it or bookmark it.\n\n' +
'Pressing `SHIFT+CTRL+o` or `SHIFT+CMD+o` opens a list of currently open items. You can go to an open item from that list, or select^ive^ly close open items.\n\n' +
'If you want an item to have some extra hor^i^zon^tal space, you can click the Wide button in its toolbar, or press the `**w**` key while it is se^lect^ed and it will become twice as wide.\n\n' +
'To focus even more on an item you can click the Focus button in its toolbar, or press `SHIFT+CTRL+Enter` or `SHIFT+CMD+Enter` while it is selected. This will make it pop in front of all the other items to give it even more space, but it will obscure the rest of the work^space.\n\n' +
'Finally, you can pin an item by clicking the Pin button in its toolbar or pressing `**p**` while it is selected. This will dock the item to the right of the screen and it will stay put when you scroll the rest of the work^space.\n\n' +
'You can also move between open items by pressing `CTRL+Comma` and `CTRL+Period` to move to the previous or next open item re^spec^tively.',
    links: [],
    aliases: [],
    archived: false,
    images: []
  },
  {
    id: '000000000000000000000004',
    type: 'system',
    sortTimestamp: 0,
    name: 'SquiggleBoard: Images',
    text:
  'SquiggleBoard items can have one or more images attached. These can in turn have precisely positioned text markers that can also be links to other items, such as in a map.\n\n' +
  '> Note that there is a hard limit to the number of images an adventure can contain; see SquiggleBoard: Image Constraints.\n\n' +
  '### Adding and removing images\n' +
  'On an item, press the **Images** button or the `**m**` key to show image management controls. Here you can upload images or remove the currently selected image.\n\n' +
  '### Image navigation\n' +
  'Hovering over an image thumbnail shows various buttons for increasing or de^creasing the thumbnail size, and for nav^i^gating between images if there are more than one . You can also use the `Left` and `Right` arrow keys on the keyboard to navigate between images.\n\n' +
  '### Image showcase mode\n' +
  'Hovering over an image thumbnail shows a display icon. Pressing this icon or the `**z**` key will display the image in "showcase" mode, showing it in full screen and hiding any other in^for^ma^tion.\n\n' +
  '### Full image view\n' +
  'Clicking on an image thumbnail or pressing the `**i**` key will show the image in a full view. In this view you can also see and edit markers that have been placed on the image.\n' +
  '### Image markers\n' +
  'The full image view can have overlayed text *markers*, with or without an ac^com^pa^ny^ing dot. These markers can also be links to other items. Clicking on such a link closes the full image view and opens the linked item, unless you check the **Keep open** option in the tool^bar, in which case links will be quietly opened in the background without closing the view.\n\n' +
  'Press the **Edit markers** button while in full image view to go into marker editing mode. Here you can click on the image to place a marker, and either choose an item to link to or enter a simple text value. If linking to an item you can also override the title of the link. To remove an existing link, click on the link and then press the "Delete selected" button that appears in the tool^bar.\n\n' +
  'Markers can show a dot at the location in the image where you clicked to add them, and you can choose to have the text to the left or right of that dot. If you do not want a dot, the text will instead be centered on the coordinates you chose.',
    links: [],
    aliases: [],
    archived: false,
    images: []
  },
  {
    id: '000000000000000000000005',
    type: 'system',
    sortTimestamp: 0,
    name: 'SquiggleBoard: Keyboard Commands',
    text:
'### Global commands\n' +
'| `SHIFT\xa0+\xa0CTRL\xa0+\xa0**f**` , `SHIFT\xa0+\xa0CMD\xa0+\xa0**f**`| Search items |\n' +
'| `SHIFT\xa0+\xa0CTRL\xa0+\xa0**d**` , `SHIFT\xa0+\xa0CMD\xa0+\xa0**d**`| Clear workspace |\n' +
'| `SHIFT\xa0+\xa0CTRL\xa0+\xa0**i**` , `SHIFT\xa0+\xa0CMD\xa0+\xa0**i**`| Create new item |\n' +
'### Selected item commands\n' +
'#### In any mode\n' +
'| `SHIFT+CTRL+Enter` , `SHIFT+CMD+Enter`| Focus item |\n' +
'| `**w**` | Toggle wide item |\n' +
'#### In view mode\n' +
'| `**e**` | Edit item |\n' +
'| `**s**` | Star/unstar item |\n' +
'| `**n**` | Edit item names and aliases |\n' +
'| `**p**` | Pin/unpin item |\n' +
'| `**q**` | Close item |\n' +
'| `Delete` | Delete item |\n' +
'| `Up/Down Arrow` | Scroll item text |\n' +
'| `Page Up/Page Down` | Scroll item text faster |\n' +
'| `**m**` | Show/hide image management |\n' +
'| `**i**` | Open currently shown image |\n' +
'| `**z**` | Show current image in showcase mode |\n' +
'| `Left/Right Arrow` | Move between images |\n' +
'#### In edit mode\n' +
'| `CTRL\xa0+\xa0Space` | Autocomplete suggested item name |\n' +
'| `CTRL\xa0+\xa0Enter` , `CMD\xa0+\xa0Enter` | Save item and keep editing |\n' +
'| `Esc` | Cancel editing |\n\n' +
'#### In link mode\n' +
'| `**o**` | Open all links |\n' +
'| `Esc` | Exit link mode |\n\n' +
'### Workspace commands\n' +
'| `CTRL\xa0+\xa0Comma / Period` | Select previous/^next open item |\n',
    links: [],
    aliases: [],
    archived: false,
    images: []
  },
  {
    id: '000000000000000000000006',
    type: 'system',
    sortTimestamp: 0,
    name: 'SquiggleBoard: Changelog',
    text:
'*For help and intro, see SquiggleBoard*.\n' +
'#### 2026-08-17\n' +
'*Hover preview*: When hovering the mouse pointer over an item link in an item description, you now get a short preview of the linked item without opening it.\n' +
'#### 2026-08-12\n' +
'- Individual items can opt out of being linked to by their canonical name (aliases will still be linked to).\n' +
'- Individual items can be configured to require correct letter casing for links.\n' +
'- The children of an item are now visible in the item\'s read view.\n\n' +
'#### 2026-08-11\n' +
'Implicit links are now visible in the edit view while editing an item, and you can click the icon next to the link to open the corresponding linked item.\n\n' +
'#### 2026-08-03\n' +
'Support for Markdown tables, with some tweaks.\n\n' +
'#### 2026-07-20\n' +
'Implicit links in text are now color coded based on linked item type.\n\n' +
'#### 2026-07-09\n' +
'Better layout on tablets and phones. The left-hand menu becomes a drawer and on really narrow screens open items become stacked vertically without nested scrollbars.\n\n' +
'#### 2026-05-07\n' +
'Added simple graph view.\n\n' +
'#### 2026-03-16\n' +
'The application now has lightweight collaboration support. If you or others have the adventure open in multiple browser windows or tabs, changes made in one will be reflected in the others within a few seconds. However if multiple people are editing the exact same item simultaneously, the last save wins, and there is no warning.\n\n' +
'#### 2026-02-25\n' +
'You can now see a navigable, filterable list of open items by pressing `SHIFT+CTRL+o` or `SHIFT+CMD+o`. This is useful when you have a lot of items open.\n\n' +
'Clicking an item link in the text of an item now jumps to the linked item and makes it the selected item. Hold SHIFT while clicking to open a link without changing the currently selected item (old behavior).\n\n' +
'Made item names stand out more from the rest of the item text, using type-based color and more padding.\n\n' +
'Items now flash more noticably when opened from links, search, etc, especially if they have to scroll into view.\n\n' +
'#### 2026-02-24\n' +
'Moved from a flex-based to a grid-based layout and made the minimum width of an item slightly narrower. The work^space still dynamically chooses the number of columns to show based on available space, but any extra space is now distributed between columns by allowing them to widen a bit.\n\n' +
'#### 2026-02-23\n' +
'Added support for application information boxes like this. See \n- SquiggleBoard\n- SquiggleBoard: Links\n- SquiggleBoard: Text Formatting\n- SquiggleBoard: Item Management\n- SquiggleBoard: Images\n- SquiggleBoard: Keyboard Commands\n- SquiggleBoard: Privacy, Security, Legal.\n\n' +
'#### 2026-02-11\n' +
'Added much better support for keyboard commands. See SquiggleBoard: Keyboard Commands.\n' +
'#### 2026-02-10\n' +
'Added support for multiple images on an item.',
    links: [],
    aliases: [],
    archived: false,
    images: []
  },
  {
    id: '000000000000000000000007',
    type: 'system',
    sortTimestamp: 0,
    name: 'SquiggleBoard: Privacy, Security, Legal',
    text:
'#### In effect as per 2026-02-23\n\n' +
'SquiggleBoard ("the application") is main^tain^ed and operated by Dozenal Technology AB located in Stockholm, Sweden ("the company"). The company has **no** affiliation with any table^top RPG publisher. Any trade^marked names or other intellectual property mentioned in the app are the property of their respective owners.\n\n' +
'The company, through this application, **does not** knowlingly collect any personal in^for^ma^tion, and **does not** share entered information with any third party. **No** reg^is^tered account or personal information is required to use the application. **No** cookies or other local per^sis^tence tech^nol^o^gy is used to store data on the user’s device. The company dis^cour^ages you from entering any personal in^for^ma^tion into the ap^pli^ca^tion.\n\n' +
'All data entered in a S\\quiggleBoard Adventure is stored on the company’s servers in the European Union. The ap^pli^ca^tion does **not** implement access control; **anyone** with the adventure link can access and modify the adventure data. Be cautious about sharing the link to your adventure.\n\n' +
'The company **cannot** guarantee the se^cu^ri^ty of the data, and **does not** take re^spon^si^bil^i^ty for any loss of data. Make sure to back up your data regularly by using the **Download** feature reachable from the top toolbar.\n\n' +
'The company reserves the right to modify, censor or remove any content entered into the app for any reason without prior notice or justification. **Image uploads are re^view^ed regularly**.\n\n' +
'Questions, comments, or concerns can be e-mailed to `[squiggleboard@dozenal-technology.com](mailto:squiggleboard@dozenal-technology.com)`',
    links: [],
    aliases: [],
    archived: false,
    images: []
  },
  {
    id: '000000000000000000000008',
    type: 'system',
    sortTimestamp: 0,
    name: 'SquiggleBoard: Image Constraints',
    text:
'***To prevent abuse of this free service, there are limits placed on the usage of images.***\n\n' +
'**There is a hard limit** to the number of images an adventure can contain. If you reach this limit and you want to raise it, you can send a request to [squiggleboard@dozenal-technology.com](mailto:squiggleboard@dozenal-technology.com) citing your adventure ID from the URL. We will review your usage of the application to determine if it looks serious, and poss^ibly raise the limit. Having your limit raised is not a right.\n\n' +
'In addition to this there is a **size limit** to each image file. The type of image is also re^strict^ed to common formats such as PNG, JPEG, GIF, WEBP, SVG and AVIF.\n\n' +
'**Images are reviewed regularly**, and if you upload inappropriate content your images or your entire adventure may be removed with^out prior notice or a pos^si^bil^i^ty to appeal. Since a lot of TTRPGs deal with mature themes, we do not have a strict policy on what is con^sid^ered inappropriate, but we do not allow content that is clearly illegal or ex^treme^ly offensive.',
    links: [],
    aliases: [],
    archived: false,
    images: []
  },
  {
    id: '000000000000000000000009',
    type: 'system',
    sortTimestamp: 0,
    name: 'SquiggleBoard: Links',
    text:
'SquiggleBoard uses wiki-like linking: simply mentioning another item by name or alias, such as SquiggleBoard: Keyboard Commands, will create a link to that item. If multiple matches are possible, the longest one wins. The link matching is case-insensitive.\n\n' +    
'You can also create links explicitly by using the format `\\[Changelog](S\\quiggleBoard: Changelog)` to produce this: [Changelog](SquiggleBoard: Changelog). The link text can be different from the item name or alias.\n\n' +
'To create an external link, use the format `\\[Example](https://example.com)` to produce this: ' +
'[Example](https://example.com)',
    links: [],
    aliases: [],
    archived: false,
    images: []
  }
];

export const isSystemItem = (itemId: string): boolean => itemId.startsWith('00000000000000000000000');